import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './BiExplotacionDatosPage.css';

const BiExplotacionDatosPage = ({ darkMode, toggleDarkMode }) => {
  const beneficios = [
    {
      icon: '📊',
      titulo: 'Decisiones Basadas en Datos',
      descripcion: 'Toma decisiones estratégicas fundamentadas en datos reales y actualizados, no en intuiciones.'
    },
    {
      icon: '⚡',
      titulo: 'Visibilidad en Tiempo Real',
      descripcion: 'Dashboards y reportes en tiempo real que te muestran el estado actual de tu negocio al instante.'
    },
    {
      icon: '🎯',
      titulo: 'Identificación de Oportunidades',
      descripcion: 'Descubre tendencias, patrones y oportunidades de negocio que antes pasaban desapercibidas.'
    },
    {
      icon: '💰',
      titulo: 'Optimización de Recursos',
      descripcion: 'Identifica áreas de mejora, reduce costos y optimiza la asignación de recursos con datos precisos.'
    },
    {
      icon: '📈',
      titulo: 'Mejora Continua',
      descripcion: 'KPIs y métricas que te permiten medir el progreso y ajustar estrategias de forma continua.'
    },
    {
      icon: '🔍',
      titulo: 'Análisis Predictivo',
      descripcion: 'Anticipa tendencias futuras y comportamientos del mercado con análisis predictivo y machine learning.'
    }
  ];

  const servicios = [
    {
      titulo: 'Dashboards y Visualización',
      descripcion: 'Dashboards interactivos y personalizados que muestran tus KPIs y métricas clave de forma visual e intuitiva.',
      casos: [
        'Dashboards ejecutivos en tiempo real',
        'Visualizaciones interactivas',
        'Reportes personalizados por departamento',
        'KPIs y métricas clave'
      ]
    },
    {
      titulo: 'Business Intelligence (BI)',
      descripcion: 'Plataformas BI completas que transforman tus datos en insights accionables para la toma de decisiones.',
      casos: [
        'Análisis multidimensional',
        'Cubos OLAP y data warehousing',
        'Reportes avanzados',
        'Integración con múltiples fuentes de datos'
      ]
    },
    {
      titulo: 'Análisis de Datos',
      descripcion: 'Análisis profundo de tus datos para descubrir patrones, tendencias y oportunidades de negocio.',
      casos: [
        'Análisis de ventas y clientes',
        'Análisis de rentabilidad',
        'Análisis de procesos',
        'Análisis predictivo'
      ]
    },
    {
      titulo: 'Data Warehousing',
      descripcion: 'Almacenes de datos centralizados que consolidan información de múltiples fuentes para análisis unificado.',
      casos: [
        'Consolidación de datos',
        'ETL (Extract, Transform, Load)',
        'Data lakes',
        'Integración de sistemas'
      ]
    },
    {
      titulo: 'Reportes Automatizados',
      descripcion: 'Reportes automáticos que se generan y envían periódicamente a los responsables, ahorrando tiempo.',
      casos: [
        'Reportes diarios, semanales, mensuales',
        'Envío automático por email',
        'Reportes personalizados',
        'Alertas automáticas'
      ]
    },
    {
      titulo: 'Análisis Predictivo y Machine Learning',
      descripcion: 'Modelos predictivos y machine learning para anticipar tendencias, comportamientos y resultados futuros.',
      casos: [
        'Predicción de ventas',
        'Análisis de churn',
        'Segmentación de clientes',
        'Optimización de inventario'
      ]
    }
  ];

  const tecnologias = [
    {
      nombre: 'Power BI',
      descripcion: 'Plataforma de Microsoft para visualización de datos y Business Intelligence con integración nativa con Microsoft 365.'
    },
    {
      nombre: 'Tableau',
      descripcion: 'Herramienta líder de visualización de datos con capacidades avanzadas de análisis y dashboards interactivos.'
    },
    {
      nombre: 'Qlik Sense',
      descripcion: 'Plataforma de BI con análisis asociativo que permite explorar datos de forma intuitiva y descubrir insights.'
    },
    {
      nombre: 'Python y R',
      descripcion: 'Lenguajes de programación para análisis de datos avanzado, machine learning y análisis estadístico.'
    },
    {
      nombre: 'SQL y Bases de Datos',
      descripcion: 'Consultas SQL, optimización de bases de datos y data warehousing para análisis eficiente de grandes volúmenes.'
    },
    {
      nombre: 'APIs y Conectores',
      descripcion: 'Integración de datos desde múltiples fuentes mediante APIs y conectores para consolidación de información.'
    }
  ];

  const proceso = [
    {
      paso: '1',
      titulo: 'Análisis de Necesidades',
      descripcion: 'Identificamos qué datos necesitas analizar, qué preguntas quieres responder y qué decisiones quieres mejorar.'
    },
    {
      paso: '2',
      titulo: 'Diseño de Arquitectura de Datos',
      descripcion: 'Diseñamos la arquitectura de datos: fuentes, integración, almacenamiento y estructura para análisis.'
    },
    {
      paso: '3',
      titulo: 'Integración y Consolidación',
      descripcion: 'Integramos datos de múltiples fuentes, limpiamos y consolidamos en un almacén de datos unificado.'
    },
    {
      paso: '4',
      titulo: 'Desarrollo de Dashboards y Reportes',
      descripcion: 'Desarrollamos dashboards interactivos y reportes personalizados según las necesidades de cada usuario.'
    },
    {
      paso: '5',
      titulo: 'Formación y Adopción',
      descripcion: 'Formamos a tu equipo en el uso de las herramientas BI y acompañamos la adopción para maximizar el valor.'
    }
  ];

  const casosUso = [
    {
      titulo: 'Análisis de Ventas',
      descripcion: 'Dashboards de ventas que muestran productos más vendidos, clientes más rentables, tendencias y previsiones.'
    },
    {
      titulo: 'Análisis Financiero',
      descripcion: 'Reportes financieros automatizados, análisis de rentabilidad, cash flow y proyecciones financieras.'
    },
    {
      titulo: 'Análisis de Clientes',
      descripcion: 'Segmentación de clientes, análisis de comportamiento, lifetime value y predicción de churn.'
    },
    {
      titulo: 'Optimización de Inventario',
      descripcion: 'Análisis de rotación de stock, predicción de demanda y optimización de niveles de inventario.'
    },
    {
      titulo: 'Análisis de Operaciones',
      descripcion: 'KPIs operacionales, análisis de eficiencia, tiempos de proceso y cuellos de botella.'
    },
    {
      titulo: 'Análisis de Marketing',
      descripcion: 'ROI de campañas, análisis de canales, conversión y efectividad de estrategias de marketing.'
    }
  ];

  const sectores = [
    {
      titulo: 'Retail y E-commerce',
      descripcion: 'Análisis de ventas, comportamiento de clientes, optimización de catálogo y predicción de demanda.'
    },
    {
      titulo: 'Manufactura',
      descripcion: 'Análisis de producción, eficiencia, calidad y optimización de procesos industriales.'
    },
    {
      titulo: 'Servicios Profesionales',
      descripcion: 'Análisis de rentabilidad por proyecto, utilización de recursos y eficiencia operativa.'
    },
    {
      titulo: 'Hostelería',
      descripcion: 'Análisis de ocupación, revenue management, satisfacción de clientes y optimización de precios.'
    },
    {
      titulo: 'Logística',
      descripcion: 'Análisis de rutas, eficiencia de entregas, costos logísticos y optimización de almacenes.'
    },
    {
      titulo: 'Salud',
      descripcion: 'Análisis de pacientes, eficiencia de recursos, tiempos de espera y calidad de servicio.'
    }
  ];

  return (
    <>
      <SEO 
        title="BI y explotación de datos | Business Intelligence | E-ASY"
        description="Dashboards, Business Intelligence y análisis de datos para decidir con la información que ya tienes. Servicio en remoto para empresas de toda España, desde la sede en Tenerife."
        keywords="business intelligence, BI empresas, dashboards, explotación datos, análisis datos, data warehousing, Power BI, Tableau, análisis predictivo, BI Canarias, business intelligence Tenerife, dashboards empresas, reportes automatizados, BI España"
        canonical="https://e-asy.es/servicios/bi-explotacion-datos"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`bi-explotacion-datos-page ${darkMode ? 'dark' : 'light'}`}>
        
        {/* Hero Section */}
        <section className="bi-hero">
          <div className="bi-hero-content">
            <div className="bi-hero-text">
              <h1 className="bi-hero-title">BI y Explotación de Datos</h1>
              <p className="bi-hero-subtitle">
                E-ASY transforma tus datos en <strong>decisiones inteligentes</strong>. Ofrecemos servicios de 
                <strong>Business Intelligence (BI)</strong>, <strong>dashboards</strong>, <strong>análisis de datos</strong> 
                y <strong>explotación de datos</strong> para que tomes decisiones basadas en información real.
              </p>
              <p className="bi-hero-subtitle">
                <strong>Dashboards en tiempo real</strong>, <strong>reportes automatizados</strong>, 
                <strong>análisis predictivo</strong> y <strong>data warehousing</strong>. Desde <strong>Canarias</strong> 
                ofrecemos servicios de BI para empresas en toda <strong>España</strong> y proyectos 
                <strong>internacionales</strong>.
              </p>
              <div className="bi-hero-cta">
                <Link to="/contacto" className="bi-hero-button primary">
                  Solicita una consultoría gratuita
                </Link>
                <Link to="/servicios" className="bi-hero-button secondary">
                  Ver todos los servicios
                </Link>
              </div>
            </div>
            <div className="bi-hero-visual">
              <div className="bi-hero-graphic">
                <div className="data-animation">
                  <div className="data-chart">
                    <div className="chart-bar bar-1"></div>
                    <div className="chart-bar bar-2"></div>
                    <div className="chart-bar bar-3"></div>
                    <div className="chart-bar bar-4"></div>
                    <div className="chart-bar bar-5"></div>
                  </div>
                  <div className="data-lines">
                    <div className="data-line line-1"></div>
                    <div className="data-line line-2"></div>
                    <div className="data-line line-3"></div>
                  </div>
                  <div className="data-insight">
                    <div className="insight-icon">💡</div>
                    <div className="insight-text">Insights</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Qué es Section */}
        <section className="bi-que-es">
          <div className="bi-que-es-content">
            <div className="bi-que-es-header">
              <h2 className="bi-que-es-title">¿Qué es Business Intelligence y Explotación de Datos?</h2>
              <p className="bi-que-es-subtitle">
                El <strong>Business Intelligence (BI)</strong> y la <strong>explotación de datos</strong> transforman 
                la información cruda de tu empresa en <strong>insights accionables</strong> para la toma de decisiones. 
                En lugar de tener datos dispersos en múltiples sistemas, el BI consolida, analiza y presenta la información 
                de forma visual e intuitiva.
              </p>
              <p className="bi-que-es-subtitle">
                Si tu empresa genera datos pero no los explota para tomar decisiones, estás perdiendo oportunidades. 
                El <strong>BI</strong> te permite ver el estado real de tu negocio, identificar tendencias, optimizar 
                procesos y anticipar el futuro con <strong>análisis predictivo</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Beneficios Section */}
        <section className="bi-beneficios">
          <div className="bi-beneficios-content">
            <div className="bi-beneficios-header">
              <h2 className="bi-beneficios-title">Beneficios del Business Intelligence</h2>
              <p className="bi-beneficios-subtitle">
                Ventajas de explotar tus datos para tomar mejores decisiones
              </p>
            </div>
            <div className="bi-beneficios-grid">
              {beneficios.map((beneficio, index) => (
                <div key={index} className="bi-beneficio-card">
                  <div className="beneficio-icon">{beneficio.icon}</div>
                  <h3 className="beneficio-title">{beneficio.titulo}</h3>
                  <p className="beneficio-description">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Servicios Section */}
        <section className="bi-servicios">
          <div className="bi-servicios-content">
            <div className="bi-servicios-header">
              <h2 className="bi-servicios-title">Servicios de BI y Explotación de Datos</h2>
              <p className="bi-servicios-subtitle">
                Soluciones completas para transformar tus datos en decisiones inteligentes
              </p>
            </div>
            <div className="bi-servicios-grid">
              {servicios.map((servicio, index) => (
                <div key={index} className="bi-servicio-card">
                  <h3 className="servicio-title">{servicio.titulo}</h3>
                  <p className="servicio-description">{servicio.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {servicio.casos.map((caso, idx) => (
                        <li key={idx} className="caso-item">{caso}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tecnologías Section */}
        <section className="bi-tecnologias">
          <div className="bi-tecnologias-content">
            <div className="bi-tecnologias-header">
              <h2 className="bi-tecnologias-title">Tecnologías de BI que Utilizamos</h2>
              <p className="bi-tecnologias-subtitle">
                Trabajamos con las mejores herramientas de Business Intelligence del mercado
              </p>
            </div>
            <div className="bi-tecnologias-grid">
              {tecnologias.map((tecnologia, index) => (
                <div key={index} className="bi-tecnologia-card">
                  <h3 className="tecnologia-title">{tecnologia.nombre}</h3>
                  <p className="tecnologia-description">{tecnologia.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Casos de Uso Section */}
        <section className="bi-casos">
          <div className="bi-casos-content">
            <div className="bi-casos-header">
              <h2 className="bi-casos-title">Casos de Uso del Business Intelligence</h2>
              <p className="bi-casos-subtitle">
                Aplicaciones prácticas del BI en diferentes áreas de tu negocio
              </p>
            </div>
            <div className="bi-casos-grid">
              {casosUso.map((caso, index) => (
                <div key={index} className="bi-caso-card">
                  <h3 className="caso-title">{caso.titulo}</h3>
                  <p className="caso-description">{caso.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proceso Section */}
        <section className="bi-proceso">
          <div className="bi-proceso-content">
            <div className="bi-proceso-header">
              <h2 className="bi-proceso-title">Nuestro Proceso de Implementación BI</h2>
              <p className="bi-proceso-subtitle">
                Metodología estructurada para implementar Business Intelligence en tu empresa
              </p>
            </div>
            <div className="bi-proceso-timeline">
              {proceso.map((item, index) => (
                <div key={index} className="proceso-step">
                  <div className="step-number">{item.paso}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.titulo}</h3>
                    <p className="step-description">{item.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sectores Section */}
        <section className="bi-sectores">
          <div className="bi-sectores-content">
            <div className="bi-sectores-header">
              <h2 className="bi-sectores-title">BI para Diferentes Sectores</h2>
              <p className="bi-sectores-subtitle">
                Adaptamos el Business Intelligence a las necesidades específicas de cada sector
              </p>
            </div>
            <div className="bi-sectores-grid">
              {sectores.map((sector, index) => (
                <div key={index} className="bi-sector-card">
                  <h3 className="sector-title">{sector.titulo}</h3>
                  <p className="sector-description">{sector.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bi-cta">
          <div className="bi-cta-content">
            <h2 className="bi-cta-title">¿Quieres Transformar tus Datos en Decisiones?</h2>
            <p className="bi-cta-subtitle">
              Agenda una consultoría gratuita y descubre cómo el Business Intelligence puede mejorar tu negocio
            </p>
            <div className="bi-cta-buttons">
              <Link to="/contacto" className="bi-cta-button primary">
                Solicita una consultoría gratuita
              </Link>
              <Link to="/servicios" className="bi-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>

      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default BiExplotacionDatosPage;






